/* Houndkeep hero slideshow (sections/houndkeep-hero.liquid). The photo and the text of each slide live in two stacks so
   the hero keeps its two-column layout; this script only moves the active index on both stacks.
   WCAG 2.2.2 (pause, stop, hide) and the WAI-ARIA APG carousel pattern:
   - automatic rotation stops while a MOUSE is over the hero, while KEYBOARD focus is inside it (a click or tap that
     focuses a button does not count, otherwise Play would never play again), while the browser tab is hidden, and for
     good with the pause button (its label says what it will do: "Pause" or "Play"); with prefers-reduced-motion it never
     starts by itself;
   - aria-live is "off" while the slideshow actually rotates and "polite" whenever it does not, so a slide picked with the
     arrows or dots is announced;
   - inactive slides are aria-hidden and inert, so their links cannot be reached.
   The photos of slides 2..n carry their URLs in data-hk-src/data-hk-srcset: the next photo is loaded after the page has
   loaded, and every photo shortly before it is shown, so none of them competes with the first (LCP) photo.
   Without JavaScript the first slide simply stays visible and the controls stay hidden. */
(() => {
  const init = (root) => {
    if (root.dataset.hkReady) return;
    root.dataset.hkReady = '1';
    const texts = [...root.querySelectorAll('[data-hk-slide-text]')];
    const media = [...root.querySelectorAll('[data-hk-slide-media]')];
    const n = Math.min(texts.length, media.length);
    if (n < 2) return;
    const dots = [...root.querySelectorAll('[data-hk-dot]')];
    const live = root.querySelector('[data-hk-texts]');
    const nav = root.querySelector('[data-hk-nav]');
    const pauseBtn = root.querySelector('[data-hk-pause]');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const interval = Math.max(4000, Number(root.dataset.interval) || 6000);
    let index = 0;
    let timer = 0;
    let stopped = !root.hasAttribute('data-autoplay') || reduce.matches; // the visitor's (or the merchant's) choice
    let hover = false;
    let focus = false;
    let pointerDown = false;

    const hydrate = (k) => {
      const slide = media[(k + n) % n];
      if (!slide) return;
      slide.querySelectorAll('img[data-hk-src]').forEach((img) => {
        if (img.dataset.hkSrcset) img.srcset = img.dataset.hkSrcset;
        img.src = img.dataset.hkSrc;
        img.removeAttribute('data-hk-src');
        img.removeAttribute('data-hk-srcset');
      });
    };
    const set = (el, on) => {
      el.classList.toggle('is-active', on);
      el.setAttribute('aria-hidden', on ? 'false' : 'true');
      if (on) el.removeAttribute('inert'); else el.setAttribute('inert', '');
    };
    const running = () => !stopped && !hover && !focus && !document.hidden;
    const show = (k, warmNext = true) => {
      index = (k + n) % n;
      hydrate(index);
      if (warmNext) hydrate(index + 1);
      for (let j = 0; j < n; j++) { set(texts[j], j === index); set(media[j], j === index); }
      dots.forEach((d, j) => d.setAttribute('aria-current', j === index ? 'true' : 'false'));
      // restart the progress bar of the active dot
      root.classList.remove('is-ticking'); void root.offsetWidth; if (running()) root.classList.add('is-ticking');
    };
    const schedule = () => {
      window.clearTimeout(timer);
      const on = running();
      root.classList.toggle('is-paused', !on);
      root.classList.toggle('is-ticking', on);
      if (live) live.setAttribute('aria-live', on ? 'off' : 'polite');
      if (on) timer = window.setTimeout(() => { show(index + 1); schedule(); }, interval);
    };
    const paint = () => {
      root.classList.toggle('is-stopped', stopped);
      if (pauseBtn) pauseBtn.setAttribute('aria-label', stopped ? pauseBtn.dataset.labelPlay : pauseBtn.dataset.labelPause);
    };
    const go = (k) => { show(k); schedule(); };

    if (nav) nav.hidden = false;
    paint();
    if (pauseBtn) pauseBtn.addEventListener('click', () => { stopped = !stopped; paint(); schedule(); });
    root.querySelector('[data-hk-prev]')?.addEventListener('click', () => go(index - 1));
    root.querySelector('[data-hk-next]')?.addEventListener('click', () => go(index + 1));
    dots.forEach((d) => d.addEventListener('click', () => go(Number(d.dataset.hkDot) || 0)));

    // a real mouse only: a tap on a phone leaves a "sticky" hover behind that would freeze the slideshow for good
    root.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { hover = true; schedule(); } });
    root.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') { hover = false; schedule(); } });
    // keyboard focus only: focus that follows a pointer press (click/tap on Play, a dot, an arrow) does not pause
    root.addEventListener('pointerdown', () => { pointerDown = true; window.setTimeout(() => { pointerDown = false; }, 0); });
    root.addEventListener('focusin', (e) => {
      let keyboard = !pointerDown;
      try { keyboard = keyboard && e.target.matches(':focus-visible'); } catch (_) { /* older browsers: keep the pointer test */ }
      focus = keyboard;
      schedule();
    });
    root.addEventListener('focusout', (e) => { if (!root.contains(e.relatedTarget)) { focus = false; schedule(); } });
    const onVisibility = () => schedule();
    document.addEventListener('visibilitychange', onVisibility);
    reduce.addEventListener?.('change', () => { if (reduce.matches) { stopped = true; paint(); schedule(); } });

    // swipe on the photo (touch only, so a mouse click on the product tag still works)
    const area = root.querySelector('[data-hk-media]');
    let x0 = null;
    let y0 = 0;
    area?.addEventListener('touchstart', (e) => { const t = e.touches[0]; x0 = t.clientX; y0 = t.clientY; }, { passive: true });
    area?.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - x0;
      const dy = t.clientY - y0;
      x0 = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) go(index + (dx < 0 ? 1 : -1));
    }, { passive: true });

    // the Shopify theme editor: show the block that is being edited and stop rotating while editing
    root.addEventListener('shopify:block:select', (e) => {
      const k = texts.findIndex((t) => t.contains(e.target) || t === e.target);
      stopped = true; paint();
      if (k >= 0) show(k);
      schedule();
    });
    // the theme editor replaces the section's HTML: stop this instance so no timer keeps running on the old DOM
    const unload = (e) => {
      if (!e.target.contains(root)) return;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('shopify:section:unload', unload);
    };
    document.addEventListener('shopify:section:unload', unload);

    // the next photo loads once the page itself has loaded
    const warm = () => (window.requestIdleCallback ? window.requestIdleCallback(() => hydrate(index + 1)) : window.setTimeout(() => hydrate(index + 1), 200));
    if (document.readyState === 'complete') warm(); else window.addEventListener('load', warm, { once: true });

    show(0, false); // slide 2 waits for the page's load event (warm() above)
    schedule();
  };

  const boot = () => document.querySelectorAll('[data-hk-slides]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
